import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  Input,
  input,
  OnDestroy,
  OnInit,
  Output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';

import { combineLatest, distinctUntilChanged, map, Observable, of, Subscription, switchMap, tap } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { unsubscribe } from '@fcom/core/utils';
import { ElementActions, ElementTypes, SelectOption } from '@fcom/common/interfaces';
import { LanguageService } from '@fcom/ui-translate';
import { GtmService } from '@fcom/common/gtm';
import { Category, FinnairItineraryItemFlight } from '@fcom/dapi/api/models';
import { ChipTheme, IconButtonSize, IconButtonTheme, TagTheme } from '@fcom/ui-components';
import { getIconForServiceCategory } from '@fcom/dapi/utils';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';
import { findFirstFocusableElement } from '@fcom/ui-components/utils/dom.utils';

import { SeatMapTraveler } from '../../../interfaces';
import { getSeatMapFlight } from '../../../utils';

@Component({
  selector: 'fin-seat-passengers',
  templateUrl: './seat-passengers.component.html',
  styleUrls: ['./seat-passengers.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SeatPassengersComponent implements OnInit, OnDestroy {
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private formBuilder = inject(FormBuilder);
  private languageService = inject(LanguageService);
  private gtmService = inject(GtmService);
  private themeService = inject(FinnairThemeService);
  protected getIconForServiceCategory = getIconForServiceCategory;
  protected readonly isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  @Input() passengers$: Observable<SeatMapTraveler[]>;
  @Input() selectedSeatsByPax$: Observable<Record<string, string>>;
  @Input() selectedBundleCategoriesByPax$: Observable<Record<string, Category[]>>;
  @Input() flight: FinnairItineraryItemFlight;
  activePax = input<SeatMapTraveler>();

  @Output() selectPax: EventEmitter<string> = new EventEmitter();

  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ChipTheme = ChipTheme;
  readonly TagTheme = TagTheme;

  passengerForm: FormGroup;
  options$: Observable<SelectOption[]>;
  subscriptions: Subscription = new Subscription();

  constructor() {
    effect(() => {
      const paxId = this.activePax()?.id;
      if (paxId && this.passengerForm) {
        this.passengerForm.get('activeTravelerId')?.setValue(paxId, { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.passengerForm = this.formBuilder.group({
      activeTravelerId: new FormControl(null),
    });
    this.createForm();

    this.subscriptions.add(
      this.passengerForm
        .get('activeTravelerId')
        .valueChanges.pipe(distinctUntilChanged())
        .subscribe((activeTravelerId) => {
          if (activeTravelerId !== this.activePax()?.id) {
            this.selectPax.emit(activeTravelerId);
          }
        })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }

  createForm(): void {
    this.options$ = combineLatest([
      this.languageService.translate('seatMap.and'),
      this.passengers$,
      this.selectedSeatsByPax$,
      this.selectedBundleCategoriesByPax$,
    ]).pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      tap(() => {
        this.passengerForm.get('activeTravelerId')?.setValue(this.activePax()?.id);
      }),
      switchMap(([withInfantLabel, passengers, selectedSeats, selectedBundleCategories]) => {
        const categories = Array.from(
          new Set(passengers.map((passenger) => selectedBundleCategories[passenger.id]?.[0]).filter(Boolean))
        );
        const bundleTranslations$ = categories.length
          ? combineLatest(
              categories.map((category) =>
                this.languageService
                  .translate(`ancillaries.${category}.extraTitle`)
                  .pipe(map((translation) => ({ category, translation })))
              )
            )
          : of([]);

        return bundleTranslations$.pipe(
          map((bundleTranslations) =>
            passengers.map((passenger) => {
              const paxName = `${passenger.traveler.firstName} ${passenger.traveler.lastName}`;
              const paxNameWithInfant = `${paxName} ${withInfantLabel} ${passenger.withInfantFullName}`;
              const seatNumber = selectedSeats[passenger.id] ? `, ${selectedSeats[passenger.id]}` : '';
              const bundleCategory = selectedBundleCategories[passenger.id]?.[0];
              const bundleTitle = bundleCategory
                ? ` + ${bundleTranslations.find((bt) => bt.category === bundleCategory)?.translation || ''}`
                : '';

              return {
                name: `${passenger.withInfant ? paxNameWithInfant : paxName}${seatNumber}${bundleTitle}`,
                value: passenger.id,
              };
            })
          )
        );
      })
    );
  }

  handleClick(passengerId: string, index: number): void {
    this.selectPax.emit(passengerId);

    this.gtmService.seatMapEvent(`pax-select-${index + 1}`, ElementTypes.BUTTON, '', ElementActions.CLICK, {
      flightInfo: getSeatMapFlight(this.flight),
    });
  }

  focusPassenger(passengerId: string): void {
    const passengerControl = Array.from(
      this.elementRef.nativeElement.querySelectorAll<HTMLElement>('[data-pax-id]')
    ).find((element) => element.dataset.paxId === passengerId);
    const focusTarget = passengerControl?.matches('button')
      ? passengerControl
      : passengerControl && findFirstFocusableElement(passengerControl);

    focusTarget?.focus();
    if (this.elementRef.nativeElement.ownerDocument.activeElement !== focusTarget) {
      findFirstFocusableElement(this.elementRef.nativeElement)?.focus();
    }
  }
}
